import cn from 'classnames';
import React, { CSSProperties, HTMLAttributes, ReactElement, ReactNode } from 'react';
import './Flex.scss';

/** Allowed HTML elements for Flex container */
export type FlexElement =
  | 'div'
  | 'section'
  | 'article'
  | 'main'
  | 'aside'
  | 'nav'
  | 'header'
  | 'footer'
  | 'span'
  | 'ul'
  | 'ol'
  | 'li';

export interface FlexProps extends Omit<HTMLAttributes<HTMLElement>, 'style'> {
  /** Content to render inside the flex container */
  children?: ReactNode;
  /** HTML element to render as */
  as?: FlexElement;
  /** Flex direction: 'row' (default) or 'column' */
  direction?: 'row' | 'column';
  /** Gap between children (CSS value, e.g., '8px', '1rem', 'var(--spacingHorizontalS)') */
  gap?: string;
  /** Cross-axis alignment (CSS align-items value) */
  align?: CSSProperties['alignItems'];
  /** Main-axis alignment (CSS justify-content value) */
  justify?: CSSProperties['justifyContent'];
  /** Allow items to wrap to next line */
  wrap?: boolean;
  /** Fill available width and height (100%) */
  fill?: boolean;
  /** Use inline-flex instead of flex */
  inline?: boolean;
  /** Additional inline styles */
  style?: CSSProperties;
}

/**
 * Flex component for flexible box layouts.
 * Uses direct CSS values - no abstractions.
 *
 * @deprecated Do not use in new code. Use the global.css layout utility classes
 * instead (`flex`, `flex-col`, `gap-*`, `items-*`, `justify-*`, `w-full`,
 * `flex-1`, …) from `shared-frontend/components/src/styles/_utils.scss`, e.g.
 * `<div className="flex items-center justify-between gap-2">`. Existing usages
 * remain functional; migration of those is tracked separately.
 *
 * @example
 * <Flex direction="column" gap="8px">
 *   <div>Item 1</div>
 *   <div>Item 2</div>
 * </Flex>
 *
 * @example
 * <Flex align="center" justify="space-between">
 *   <div>Left</div>
 *   <div>Right</div>
 * </Flex>
 */
function FlexContainer({
  children,
  as: Element = 'div',
  direction = 'row',
  gap,
  align,
  justify,
  wrap = false,
  fill = false,
  inline = false,
  className,
  style,
  ...rest
}: FlexProps) {
  const flexClassName = cn(
    'aix-flex',
    {
      'aix-flex--column': direction === 'column',
      'aix-flex--wrap': wrap,
      'aix-flex--fill': fill,
      'aix-flex--inline': inline,
    },
    className,
  );

  const flexStyle: CSSProperties = {
    ...style,
    ...(gap && { gap }),
    ...(align && { alignItems: align }),
    ...(justify && { justifyContent: justify }),
  };

  return (
    <Element className={flexClassName} style={flexStyle} {...rest}>
      {children}
    </Element>
  );
}

FlexContainer.displayName = 'Flex';

// ============================================================================
// Flex.Item Subcomponent
// ============================================================================

/**
 * @deprecated Deprecated together with `Flex` — see {@link FlexItem}.
 */
export interface FlexItemProps extends Omit<HTMLAttributes<HTMLElement>, 'style'> {
  /** Content to render */
  children?: ReactNode;
  /** HTML element to render as */
  as?: FlexElement;
  /** flex-grow value (true = 1) */
  grow?: boolean | number;
  /** flex-shrink value (true = 1, false = 0) */
  shrink?: boolean | number;
  /** flex-basis value */
  basis?: string;
  /** Override alignment for this item (CSS align-self value) */
  align?: CSSProperties['alignSelf'];
  /** Push item to the end (margin-inline-start: auto) */
  push?: boolean;
  /** Additional inline styles */
  style?: CSSProperties;
}

/**
 * Flex.Item component for individual flex child configuration.
 *
 * @deprecated Deprecated together with `Flex`. Use the global.css utility
 * classes on the child element instead (`flex-1`, `flex-auto`, `flex-none`,
 * `ms-auto`, …). `push` sets the *logical* `margin-inline-start`, so its
 * replacement is `ms-auto`, not the physical (LTR-only) `ml-auto`.
 *
 * @example
 * <Flex>
 *   <Flex.Item grow>Grows to fill</Flex.Item>
 *   <div>Fixed</div>
 * </Flex>
 */
function FlexItem({
  children,
  as: Element = 'div',
  grow,
  shrink,
  basis,
  align,
  push = false,
  className,
  style,
  ...rest
}: FlexItemProps) {
  const itemClassName = cn('aix-flex-item', className);

  const itemStyle: CSSProperties = {
    ...style,
    ...(grow !== undefined && {
      flexGrow: grow === true ? 1 : grow === false ? 0 : grow,
    }),
    ...(shrink !== undefined && {
      flexShrink: shrink === true ? 1 : shrink === false ? 0 : shrink,
    }),
    ...(basis && { flexBasis: basis }),
    ...(align && { alignSelf: align }),
    ...(push && { marginInlineStart: 'auto' }),
  };

  return (
    <Element className={itemClassName} style={itemStyle} {...rest}>
      {children}
    </Element>
  );
}

FlexItem.displayName = 'Flex.Item';

/**
 * Almost every usage writes `<Flex.Item>`, which resolves through the `Item`
 * property rather than through the named `FlexItem` export. A `@deprecated` tag
 * on the `FlexItem` function (or on a plain `Flex.Item = FlexItem` expando
 * assignment) does not reach that property — verified against the TypeScript
 * 5.9 language service, which reports the deprecation only for an explicitly
 * declared property. Hence this interface: it is what makes `<Flex.Item>` show
 * up struck through in the editor.
 */
interface FlexComponent {
  (props: FlexProps): ReactElement;
  displayName?: string;
  /**
   * @deprecated Deprecated together with `Flex` — see {@link FlexItem}.
   */
  Item: typeof FlexItem;
}

/**
 * Flex component for flexible box layouts.
 * Uses direct CSS values - no abstractions.
 *
 * @deprecated Do not use in new code. Use the global.css layout utility classes
 * instead (`flex`, `flex-col`, `gap-*`, `items-*`, `justify-*`, `w-full`,
 * `h-full`, `flex-1`, …) from `shared-frontend/components/src/styles/_utils.scss`,
 * e.g. `<div className="flex items-center justify-between gap-2">`. Note that
 * `fill` sets width *and* height, so it takes both `w-full h-full`. Existing
 * usages remain functional; migration of those is tracked separately.
 *
 * @example
 * <Flex direction="column" gap="8px">
 *   <div>Item 1</div>
 *   <div>Item 2</div>
 * </Flex>
 *
 * @example
 * <Flex align="center" justify="space-between">
 *   <div>Left</div>
 *   <div>Right</div>
 * </Flex>
 */
export const Flex: FlexComponent = Object.assign(FlexContainer, { Item: FlexItem });

export { FlexItem };
